'use client'

import { format, isValid, setHours, setMinutes } from 'date-fns'
import { de } from 'date-fns/locale'
import { Calendar as CalendarIcon } from 'lucide-react'
import * as React from 'react'
import { type DateRange } from 'react-day-picker'

import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

interface DateRangePickerProps {
  from?: Date
  to?: Date | null
  onDateRangeChange?: (range: { from: Date | undefined; to: Date | undefined | null }) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  showIcon?: boolean
  dateFormat?: string
  footer?: React.ReactNode
  showTime?: boolean
}

export function DateRangePicker({
  from,
  to,
  onDateRangeChange,
  placeholder = 'Wähle einen Zeitraum',
  className,
  disabled,
  showIcon = true,
  dateFormat = 'dd.MM.yy',
  footer,
  showTime = false,
}: DateRangePickerProps) {
  const [range, setRange] = React.useState<DateRange | undefined>({
    from: from && isValid(from) ? from : undefined,
    to: to && isValid(to) ? to : undefined,
  })

  const [month, setMonth] = React.useState<Date | undefined>(
    from && isValid(from) ? from : new Date()
  )

  const [fromTime, setFromTime] = React.useState<string>(
    from && isValid(from) ? format(from, 'HH:mm') : '00:00'
  )
  const [toTime, setToTime] = React.useState<string>(
    to && isValid(to) ? format(to, 'HH:mm') : '23:59'
  )

  React.useEffect(() => {
    const validFrom = from && isValid(from) ? from : undefined
    const validTo = to && isValid(to) ? to : undefined

    setRange(validFrom || validTo ? { from: validFrom, to: validTo } : undefined)
    if (from && isValid(from)) {
      setFromTime(format(from, 'HH:mm'))
      setMonth(from)
    }
    if (to && isValid(to)) {
      setToTime(format(to, 'HH:mm'))
    }
  }, [from, to])

  const handleDateChange = (selectedRange: DateRange | undefined) => {
    let newFrom = selectedRange?.from
    let newTo = selectedRange?.to

    if (showTime && newFrom) {
      const [hours, minutes] = fromTime.split(':').map(Number)
      newFrom = setHours(setMinutes(newFrom, minutes), hours)
    }

    if (showTime && newTo) {
      const [hours, minutes] = toTime.split(':').map(Number)
      newTo = setHours(setMinutes(newTo, minutes), hours)
    }

    setRange(selectedRange)
    onDateRangeChange?.({
      from: newFrom,
      to: newTo || null,
    })
  }

  const handleTimeChange = (type: 'from' | 'to', time: string) => {
    if (type === 'from') {
      setFromTime(time)
      if (range?.from) {
        const [hours, minutes] = time.split(':').map(Number)
        const newFrom = setHours(setMinutes(range.from, minutes), hours)
        onDateRangeChange?.({
          from: newFrom,
          to: range.to || null,
        })
      }
    } else {
      setToTime(time)
      if (range?.to) {
        const [hours, minutes] = time.split(':').map(Number)
        const newTo = setHours(setMinutes(range.to, minutes), hours)
        onDateRangeChange?.({
          from: range.from,
          to: newTo,
        })
      }
    }
  }

  const formatDateRange = () => {
    if (range?.from && range?.to && isValid(range.from) && isValid(range.to)) {
      if (showTime) {
        return `${format(range.from, dateFormat, { locale: de })}, ${fromTime} Uhr - ${format(range.to, dateFormat, { locale: de })}, ${toTime} Uhr`
      }
      return `${format(range.from, dateFormat, { locale: de })} - ${format(range.to, dateFormat, { locale: de })}`
    } else if (range?.from && isValid(range.from)) {
      if (showTime) {
        return `${format(range.from, dateFormat, { locale: de })}, ${fromTime} Uhr`
      }
      return format(range.from, dateFormat, { locale: de })
    }
    return placeholder
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant={'outline'}
          className={cn(
            'w-full justify-start pl-2 pr-0 text-left text-sm font-normal sm:w-auto md:px-4 md:py-2',
            !range && 'text-muted-foreground',
            className
          )}
          disabled={disabled}
        >
          {showIcon && <CalendarIcon className="mr-1 h-3 w-3 md:mr-2 md:h-4 md:w-4" />}
          <span className="truncate">{formatDateRange()}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="range"
          selected={range}
          onSelect={handleDateChange}
          month={month}
          onMonthChange={setMonth}
          numberOfMonths={1}
          showWeekNumber
          footer={footer}
        />
        {showTime && (
          <div className="grid grid-cols-4 place-content-start items-center gap-2 border-t p-3">
            <Label htmlFor="from-time" className="max-w-4 text-sm font-medium">
              Startzeit:
            </Label>
            <Input
              id="from-time"
              type="time"
              value={fromTime}
              onChange={(e) => handleTimeChange('from', e.target.value)}
              className="col-span-3 w-32"
            />
            <Label htmlFor="to-time" className="max-w-4 text-sm font-medium">
              Endzeit:
            </Label>
            <Input
              id="to-time"
              type="time"
              value={toTime}
              onChange={(e) => handleTimeChange('to', e.target.value)}
              className="col-span-3 w-32"
            />
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
